
<template>
    <div class="dropzone" :class="{ 'dropzone--single' : maxNumberOfFiles === 1}" ref="dropzone" ></div>
</template>

<script>

import { getAccessToken, retryAfterAuthFail } from 'js/api/auth'
import track from 'js/helpers/analytics'
import _remove from 'lodash/remove'
import { transform } from 'js/vuex/helpers'
import notify from 'js/components/notifications/notificationsMixin'
import Dropzone from 'dropzone'
Dropzone.autoDiscover = false

export default {
    props: {
        maxNumberOfFiles: {
            type: Number,
            default: 3,
        },
        url: {
            type: String,
            required: true,
        },
        message: {
            type: String,
            default: 'Upload afbeelding',
        },

        config: {
            type: Object,
            default: () => ({})
        },

        extractResultFromResponse: {
            type: Function,
            default: (response) => transform(response).images[0]
        },

        // array of objects { name: '', thumb: '', ...(custom props) }
        value: { }

    },

    mixins: [ notify ],

    data: function() {
        return {
            uploaded: [],
            isResetting: false,
        }
    },


    methods: {
        register: function(el) {
            this.currentDropzone = new Dropzone(el, Object.assign( this.defaultConfig(), this.config ) )
            Dropzone.confirm = (question, accepted, rejected) => {
                this.$alert(question, [
                    { name: 'nee', action: rejected }, // rejected might not be defined, but alerter does not care :)
                    { name: 'ja', action: accepted },
                ])
            }
        },

        defaultConfig: function() {
            const vm = this
            return {
                url: this.url,
                paramName: 'image',
                acceptedFiles: 'image/*',
                maxFilesize: 6,
                addRemoveLinks: true,
                maxFiles: this.maxNumberOfFiles,

                // forceFallback: true, // TODO test fallback

                dictDefaultMessage: this.message,
                dictFallbackMessage: this.message,
                dictFallbackText: '',
                dictCancelUpload: 'Annuleren',
                dictCancelUploadConfirmation: 'Weet je zeker dat je de upload wilt annuleren?',
                dictRemoveFile: 'Verwijderen',
                dictRemoveFileConfirmation: 'Weet je zeker dat je deze foto wilt verwijderen?',

                dictInvalidFileType: 'Onjuist bestandsformaat, je kunt alleen jpg/png/gif uploaden!',
                dictFileTooBig: 'Helaas, deze foto is te groot ({{filesize}}MB). De foto mag maximaal {{maxFilesize}}MB zijn.',
                dictResponseError: 'Oeps, er is iets mis gegaan met de upload :( Probeer het nog eens.',
                dictMaxFilesExceeded: 'Dat zijn wel erg veel foto\'s! Je kunt er maximaal ' +  this.maxNumberOfFiles + ' uploaden.',

                accept: (file, done) => {
                    done()
                },

                init: function() {
                    // `this` refers to dropzone
                    vm.registerEvents(this)
                    vm.injectFiles(this)
                    vm.$emit('initialized', this)
                },

            }
        },

        registerEvents: function(dz) {

            dz.on('addedfile', file => {

                file.previewElement.addEventListener('click', e => {
                    e.preventDefault()
                    e.stopPropagation()

                    const upload = this.uploaded.find( u => u.file === file)
                    if ( upload != null ) {
                        this.$emit('clicked', file, upload.data)
                    }

                });

            })

            // Add token to authenticate user
            dz.on('sending', (file, xhr) => {
                this.$emit('sending', file, xhr)
                xhr.setRequestHeader('Authorization', 'Bearer ' + getAccessToken())
            })

            dz.on('success', (file, response) => {

                const result = this.extractResultFromResponse(response)

                this.uploaded.push({
                    file: file,
                    data: result
                })
                this.updated()
                this.$emit('success', file, result)
            })

            dz.on('error', (file, error, xhr) => {

                if ( !file.accepted ) {
                    track.event('dropzone', 'unaccepted', error)
                } else if ( xhr != null ) {

                    // make sure xhr object is detected as isResponse in analytics
                    xhr.url = this.url
                    track.exception('Could not upload Dropzone file', xhr, { file: file.name, message: error })

                    if ( xhr.status == 401 ) {
                        // notify auth, but don't retry anything
                        retryAfterAuthFail()
                            .catch( err => {
                                track.exception('Could not re-auth after upload fail', err, { file: file.name, message: error })
                                this.$error('Kan geen verbinding krijgen met de server. Probeer opnieuw in te loggen.', { id: 'auth' } )
                            })
                    }

                    // friendly error (otherwise dropzone just displays the response '[object]')
                    const errorDisplay = file.previewElement.querySelectorAll('[data-dz-errormessage]')[0]
                    if ( errorDisplay != null ) {
                        errorDisplay.innerHTML = xhr.status == 422
                            ? JSON.parse(xhr.response).errors
                                .map( e => e.detail )
                                .filter( e => e != null && e.length > 0)
                                .join(' ')
                            : dz.options.dictResponseError
                    }

                } else if ( file.status !== 'canceled') {
                    track.exception('Could not upload Dropzone file', { error }, { file: file.name })
                }

                this.$emit('error', file, error)
            })

            dz.on('canceled', file => {
                track.event('dropzone', 'cancel', file.name)
            })

            dz.on('removedfile', file => {
                if ( this.isResetting ) {
                    return
                }

                const removed = _remove(this.uploaded, upload => upload.file === file)
                if ( removed.length > 0 ) {
                    track.event('dropzone', 'remove', file.name)
                    this.updated()
                    this.$emit('removed', file, removed[0].data)
                }
            })

            // dz.on('maxfilesreached', () => {})

            // dz.on('complete', file => console.log('Completed uploading', file.name))
            // dz.on('addedfile', file => console.log('Added file', file.name))
            // dz.on('maxfilesexceeded', file => console.error('Max files exceeded'))

        },

        injectFiles: function(dz) {
            const thumbsToAdd = this.value instanceof Array ? this.value : (this.value ? [this.value] : [])
            thumbsToAdd.forEach(t => this.injectThumb(t, dz))
        },

        injectThumb: function(data, dz) {
            const mock = { name: data.name || data.id, size: 12345, type: 'image/jpeg', accepted: true, status: Dropzone.SUCCESS }
            dz.files.push(mock);
            dz.emit('addedfile', mock);
            dz.emit('thumbnail', mock, data.thumb);
            dz.emit('complete', mock);

            this.uploaded.push({
                file: mock,
                data: data
            })
        },

        updated: function() {
            if ( this.maxNumberOfFiles === 1 ) {
                let upload = this.uploaded[0]
                this.$emit('input', upload ? upload.data : null)
            } else {
                this.$emit('input', this.uploaded.map( u => u.data ))
            }
        },

        destroy: function() {
            if ( this.currentDropzone ) {
                this.currentDropzone.disable() // cancels current uploads
                this.isResetting = true
                this.currentDropzone.destroy()
                this.isResetting = false
            }
        },

        /**
         * Resets the Dropzone element
         */
        reset: function() {
            if ( this.currentDropzone ) {
                this.isResetting = true
                this.currentDropzone.removeAllFiles(true)
                this.uploaded = []
                this.injectFiles(this.currentDropzone)
                this.isResetting = false
            }
        }

    },

    mounted: function() {

        this.$nextTick(() => {
            this.register(this.$refs.dropzone)
        })

    },

    beforeDestroy: function() {
        this.destroy()
    },

}
</script>


<style lang="scss">

    @import 'global';
    @import 'dropzone';

</style>



// WEBPACK FOOTER //
// Dropzone.vue?090317fa